import React, { useState, useEffect } from "react";
import samhealthLogo from "../../assets/samhealth.png";
import { useNavigate, useLocation } from "react-router-dom";
import { getDashboardRouteByRole } from "../../utils/navigationUtils";
import { Mail, Lock, Eye, EyeOff, AlertCircle, Loader } from "lucide-react";
import { loginUser, getRoleDashboardRoute } from "../../services/authService";
import { useAuth } from "../../context/AuthContext";
import useNavigationGuard from "../../hooks/useNavigationGuard";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userRole, loading: authLoading } = useAuth();
  const { isGuarded } = useNavigationGuard();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Don't render the login page if user is authenticated (will be redirected by navigation guard)
  if (authLoading) {
    return null; // Loading state handled by navigation guard
  }

  if (isGuarded) {
    return null; // Will be redirected by navigation guard
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.email || !formData.password) {
      setError("Please fill in all fields");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      console.log("Attempting login with:", {
        email: formData.email,
      });

      // Call the login function from authService
      const result = await loginUser(formData.email, formData.password);

      console.log("Login result:", result);

      if (result.success) {
        const userRoles = Array.isArray(result.userData.roles) && result.userData.roles.length > 0
          ? result.userData.roles
          : (result.userData.role ? [result.userData.role] : []);

        // Check if user account is active
        if (!result.userData.isActive) {
          setError(
            "Your account has been deactivated. Please contact the administrator."
          );
          setLoading(false);
          return;
        }

        // If multiple roles, go to role select and preserve intended route if any
        if (userRoles.length > 1) {
          const intended = location.state?.from?.pathname || null;
          navigate('/select-role', { replace: true, state: { from: intended } });
          return;
        }

        // Check if there's an intended route from navigation state
        const from = location.state?.from?.pathname;
        if (from && from !== '/login' && from !== '/') {
          console.log("Redirecting to intended route after login:", from);
          navigate(from, { replace: true });
        } else {
          // Navigate to the appropriate dashboard based on user role
          const dashboardRoute = getDashboardRouteByRole(userRoles[0]);
          navigate(dashboardRoute, { replace: true });
        }
      } else {
        // Handle login failure
        setError(
          result.error ||
            "Login failed. Please check your credentials and try again."
        );
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("An unexpected error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    // TODO: Implement forgot password functionality
    alert(
      "Forgot password functionality will be implemented. Please contact your administrator for now."
    );
  };

  const handleContactAdmin = () => {
    // TODO: Navigate to contact page or show contact information
    alert(
      "Please contact your system administrator to create an account or reset your password."
    );
  };

  return (
    <div className="min-h-screen bg-gradient-corporate flex items-center justify-center p-4">
      {/* Show loading while checking authentication */}
      {authLoading ? (
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      ) : (
        <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          {/* Logo */}
          <img
            src={samhealthLogo}
            alt="SamHealth Logo"
            className="mx-auto mb-6 h-20 w-20 object-contain rounded-full shadow-card border border-gray-200 bg-white p-2"
            draggable="false"
          />
          <div className="mb-6">
            <h1 className="text-2xl font-display font-bold text-gray-900 tracking-tight">SamHealth</h1>
            <p className="text-xs font-medium text-gray-500 tracking-wide uppercase mt-1">
              Insurance Processing Platform
            </p>
          </div>
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-2">
            Welcome Back
          </h2>
          <p className="text-gray-600">Sign in to your account to continue</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-card p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Email Address *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  placeholder="Enter your email address"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Password *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleInputChange}
                  className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  placeholder="Enter your password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-start space-x-2 text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
                <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-corporate"
            >
              {loading ? (
                <>
                  <Loader className="h-5 w-5 animate-spin" />
                  <span>Signing In...</span>
                </>
              ) : (
                <span>Sign In to Dashboard</span>
              )}
            </button>

            {/* Forgot Password Link */}
            <div className="text-center">
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm text-primary-600 hover:text-primary-700 transition-colors font-medium"
                disabled={loading}
              >
                Forgot your password?
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-600">
            Don't have an account?{" "}
            <button
              onClick={handleContactAdmin}
              className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
              disabled={loading}
            >
              Contact Administrator
            </button>
          </p>
          <p className="text-xs text-gray-500 mt-4">
            © 2024 SamHealth. All rights reserved.
          </p>
        </div>
      </div>
      )}
    </div>
  );
};

export default LoginPage;